import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Page } from 'react-pdf';
import { pageRenderWidth } from '../utils/pageFit';
import { canvasPixelRatio } from '../utils/viewerSizing';

const PDFPageWrapper = React.memo(function PDFPageWrapper({ pageNumber, pageWidth, fitHeight, fitMode, zoom, onRender, renderOverlay }) {
  const [dims, setDims] = useState(null);
  const [intrinsic, setIntrinsic] = useState({ width: 595, height: 842 });
  const renderWidth = pageRenderWidth(pageWidth, fitHeight, intrinsic, fitMode, zoom);
  const renderHeight = renderWidth * intrinsic.height / intrinsic.width;
  const pixelRatio = canvasPixelRatio(renderWidth, renderHeight, window.devicePixelRatio || 1);
  const onLoad = useCallback(page => {
    const viewport = page.getViewport({ scale: 1 });
    setIntrinsic(old => old.width === viewport.width && old.height === viewport.height ? old : { width: viewport.width, height: viewport.height });
  }, []);
  const [visible, setVisible] = useState(pageNumber === 1); // first page eager
  const sentinelRef = useRef(null);

  // Keep only nearby canvases in memory. A placeholder retains exact geometry.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    if (!window.IntersectionObserver) { setVisible(true); return; }
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { root: el.closest('.studio-document-stage'), rootMargin: "300px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => setDims(null), [renderWidth, visible]);

  const onRenderSuccess = useCallback(
    (page) => {
      const viewport = page.getViewport({ scale: 1 });
      const d = {
        canvasWidth: page.width,
        canvasHeight: page.height,
        pdfWidth: viewport.width,
        pdfHeight: viewport.height,
      };
      setDims(d);
      onRender?.(pageNumber, d);
    },
    [pageNumber, onRender],
  );

  return (
    <div className="pdf-page-container" data-page-number={pageNumber} ref={sentinelRef}>
      <span className="page-label">Page {pageNumber}</span>
      <div className="canvas-wrapper" style={{ position: "relative", width: renderWidth, minHeight: renderHeight }}>
        {visible ? (
          <Page
            pageNumber={pageNumber}
            width={renderWidth}
            devicePixelRatio={pixelRatio}
            onLoadSuccess={onLoad}
            renderTextLayer={true}
            renderAnnotationLayer={true}
            onRenderSuccess={onRenderSuccess}
            loading={
              <div className="pdf-loading" style={{ width: renderWidth, height: renderHeight }}>
                <span className="spinner" />
                Rendering page…
              </div>
            }
          />
        ) : (
          <div className="pdf-loading" style={{ width: renderWidth, height: renderHeight }}>
            <span className="spinner" />
            Waiting to render…
          </div>
        )}
        {visible && dims && renderOverlay?.(pageNumber, dims)}
      </div>
    </div>
  );
});

export default PDFPageWrapper;
